import { create } from "zustand";
import type { TSavedSlide } from "@/shared/types";
import { getAdminOrgId } from "@/shared/utils/admin-org-id";
import {
  RECENTLY_USED_KEYS,
  RECENTLY_USED_MAX,
  RECENTLY_USED_SAVED_PAYLOAD_KEYS,
  type RecentlyUsedCategory,
  type RecentlyUsedSavedCategory,
} from "../constants";
import type { SlidesLibraryState } from "./types";

// Recently-used lists live in localStorage, which is shared across every
// organization in a browser. Storydoc admins switch between orgs, so an
// unscoped key leaks one org's recently-used slides into all the others
// (monday 12200733584). Scope the key by the active org id — the same value
// the API sends as the `Orgid` header — the one this tab was opened on, so a
// tab left open while the admin switches org elsewhere keeps its own list.
// Non-admins have no admin org id set, so their key is unchanged (and they
// never switch orgs, so they can't hit the leak).
const getOrgScope = (): string => getAdminOrgId();

const scopedKey = (baseKey: string): string => {
  const org = getOrgScope();
  return org ? `${baseKey}:${org}` : baseKey;
};

export const getRecentlyUsedCategory = (
  type: SlidesLibraryState["type"],
  templateTab: SlidesLibraryState["templateTab"],
): RecentlyUsedCategory | null => {
  if (type === "templates" && templateTab === "import-from-stories") return null;
  if (type === "user-slides") return "user-slides";
  if (type === "team-slides") return "team-slides";
  if (type === "layouts") return "layouts";
  if (type === "templates") return "templates";
  return null;
};

type RecentlyUsedState = {
  lists: Record<RecentlyUsedCategory, string[]>;
  savedSlides: Record<RecentlyUsedSavedCategory, TSavedSlide[]>;
  recordUse: (category: RecentlyUsedCategory, id: string) => void;
  recordSavedSlide: (
    category: RecentlyUsedSavedCategory,
    slide: TSavedSlide,
  ) => void;
  removeSavedSlide: (id: string) => void;
};

// Saved slides can be recorded under either saved category; a delete has no
// category context, so purge from both.
const SAVED_CATEGORIES: RecentlyUsedSavedCategory[] = [
  "user-slides",
  "team-slides",
];

const readIdsFromStorage = (category: RecentlyUsedCategory): string[] => {
  try {
    const raw = window.localStorage.getItem(scopedKey(RECENTLY_USED_KEYS[category]));
    if (!raw) return [];
    const parsed = JSON.parse(raw);
    if (!Array.isArray(parsed)) return [];
    return parsed
      .filter((x): x is string => typeof x === "string")
      .slice(0, RECENTLY_USED_MAX);
  } catch {
    return [];
  }
};

const writeIdsToStorage = (category: RecentlyUsedCategory, ids: string[]) => {
  try {
    window.localStorage.setItem(
      scopedKey(RECENTLY_USED_KEYS[category]),
      JSON.stringify(ids),
    );
  } catch {
    // localStorage unavailable or quota exceeded — silently ignore.
  }
};

const isSavedSlidePayload = (value: unknown): value is TSavedSlide =>
  !!value &&
  typeof value === "object" &&
  typeof (value as { _id?: unknown })._id === "string";

const readSavedFromStorage = (
  category: RecentlyUsedSavedCategory,
): TSavedSlide[] => {
  try {
    const raw = window.localStorage.getItem(
      scopedKey(RECENTLY_USED_SAVED_PAYLOAD_KEYS[category]),
    );
    if (!raw) return [];
    const parsed = JSON.parse(raw);
    if (!Array.isArray(parsed)) return [];
    return parsed.filter(isSavedSlidePayload).slice(0, RECENTLY_USED_MAX);
  } catch {
    return [];
  }
};

const writeSavedToStorage = (
  category: RecentlyUsedSavedCategory,
  slides: TSavedSlide[],
) => {
  try {
    window.localStorage.setItem(
      scopedKey(RECENTLY_USED_SAVED_PAYLOAD_KEYS[category]),
      JSON.stringify(slides),
    );
  } catch {
    // localStorage unavailable or quota exceeded — silently ignore.
  }
};

export const useRecentlyUsedStore = create<RecentlyUsedState>((set, get) => ({
  lists: {
    "user-slides": readIdsFromStorage("user-slides"),
    "team-slides": readIdsFromStorage("team-slides"),
    layouts: readIdsFromStorage("layouts"),
    templates: readIdsFromStorage("templates"),
  },
  savedSlides: {
    "user-slides": readSavedFromStorage("user-slides"),
    "team-slides": readSavedFromStorage("team-slides"),
  },
  recordUse: (category, id) => {
    if (!id) return;
    const current = get().lists[category];
    const next = [id, ...current.filter((x) => x !== id)].slice(
      0,
      RECENTLY_USED_MAX,
    );
    set((state) => ({ lists: { ...state.lists, [category]: next } }));
    writeIdsToStorage(category, next);
  },
  recordSavedSlide: (category, slide) => {
    if (!slide?._id) return;
    const state = get();
    const nextIds = [
      slide._id,
      ...state.lists[category].filter((x) => x !== slide._id),
    ].slice(0, RECENTLY_USED_MAX);
    const nextSlides = [
      slide,
      ...state.savedSlides[category].filter((s) => s._id !== slide._id),
    ].slice(0, RECENTLY_USED_MAX);
    set((prev) => ({
      lists: { ...prev.lists, [category]: nextIds },
      savedSlides: { ...prev.savedSlides, [category]: nextSlides },
    }));
    writeIdsToStorage(category, nextIds);
    writeSavedToStorage(category, nextSlides);
  },
  removeSavedSlide: (id) => {
    if (!id) return;
    // Deleting a saved slide from the DB must also evict it from the
    // recently-used lists, or the ghost lingers in "Recently used" until the
    // localStorage entry happens to roll over (monday 12796233935).
    const state = get();
    const nextLists = { ...state.lists };
    const nextSaved = { ...state.savedSlides };
    for (const category of SAVED_CATEGORIES) {
      const ids = state.lists[category].filter((x) => x !== id);
      const slides = state.savedSlides[category].filter((s) => s._id !== id);
      nextLists[category] = ids;
      nextSaved[category] = slides;
      writeIdsToStorage(category, ids);
      writeSavedToStorage(category, slides);
    }
    set({ lists: nextLists, savedSlides: nextSaved });
  },
}));
